import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { T } from '../../constants/mockData';
import { masterAPI, ticketsAPI } from '../../services/api';

const FALLBACK = [
  { label: 'Low',      color: '#718096' },
  { label: 'Medium',   color: '#f59e0b' },
  { label: 'High',     color: '#ef4444' },
  { label: 'Urgent',   color: '#dc2626' },
  { label: 'Critical', color: '#7f1d1d' },
];

const DROPDOWN_W = 200;

export default function InlinePriorityDropdown({ ticketId, value, onChange }) {
  const [open, setOpen]               = useState(false);
  const [saving, setSaving]           = useState(false);
  const [options, setOptions]         = useState(FALLBACK);
  const [portalStyle, setPortalStyle] = useState({});
  const btnRef  = useRef(null);
  const dropRef = useRef(null);

  useEffect(() => {
    masterAPI.list('priority')
      .then(rows => {
        const active = (rows || []).filter(r => r.is_active !== false);
        if (active.length) {
          setOptions(active.map(r => ({
            label: r.value,
            color: r.color || FALLBACK.find(f => f.label === r.value)?.color || '#718096',
          })));
        }
      })
      .catch(() => {});
  }, []);

  const current = options.find(p => p.label === value) || { label: value || '—', color: '#718096' };

  useEffect(() => {
    if (!open) return;
    const handler = e => {
      if (btnRef.current?.contains(e.target)) return;
      if (dropRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = useCallback(() => {
    if (open) { setOpen(false); return; }
    if (!btnRef.current) { setOpen(true); return; }
    const rect    = btnRef.current.getBoundingClientRect();
    const scrollX = window.scrollX || 0;
    const scrollY = window.scrollY || 0;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const top  = spaceBelow >= 220
      ? rect.bottom + scrollY + 4
      : rect.top + scrollY - 230;
    const spaceRight = window.innerWidth - rect.left;
    const left = spaceRight >= DROPDOWN_W + 8
      ? rect.left + scrollX
      : rect.right + scrollX - DROPDOWN_W;
    setPortalStyle({ position: 'absolute', top, left, width: DROPDOWN_W, zIndex: 9999 });
    setOpen(true);
  }, [open]);

  const handleSelect = useCallback(async (newPriority) => {
    setOpen(false);
    if (newPriority === value) return;
    setSaving(true);
    try {
      await ticketsAPI.update(ticketId, { priority: newPriority });
      onChange(newPriority);
    } catch (err) {
      console.error('InlinePriorityDropdown save error:', err);
    } finally {
      setSaving(false);
    }
  }, [ticketId, value, onChange]);

  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <button
        ref={btnRef}
        onClick={handleOpen}
        disabled={saving}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: '#F8FAFC',
          color: '#1A202C',
          border: `1px solid ${T.border}`,
          borderRadius: 99, padding: '4px 10px 4px 8px',
          fontSize: 12, fontWeight: 600, cursor: 'pointer',
          whiteSpace: 'nowrap', lineHeight: 1.3,
          opacity: saving ? 0.6 : 1,
          minWidth: 88,
        }}
      >
        <span style={{
          width: 8, height: 8, borderRadius: '50%',
          background: current.color, flexShrink: 0,
        }} />
        {saving ? '…' : (current.label || '—')}
        <svg width="9" height="6" viewBox="0 0 10 6" fill="none" style={{ flexShrink: 0, marginLeft: 2 }}>
          <path d="M1 1l4 4 4-4" stroke="#718096" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && ReactDOM.createPortal(
        <div
          ref={dropRef}
          style={{
            ...portalStyle,
            background: '#fff',
            border: `1px solid ${T.border}`,
            borderRadius: 10,
            boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
            overflow: 'hidden',
          }}
        >
          {options.map(p => {
            const isActive = p.label === value;
            return (
              <button
                key={p.label}
                onClick={() => handleSelect(p.label)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  width: '100%', padding: '9px 14px',
                  border: 'none', background: isActive ? '#F8FAFC' : 'none',
                  cursor: 'pointer', fontSize: 12, textAlign: 'left',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = T.g50; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'none'; }}
              >
                <span style={{
                  width: 9, height: 9, borderRadius: '50%',
                  background: p.color, flexShrink: 0,
                }} />
                <span style={{ fontWeight: isActive ? 700 : 500, color: '#2D3748' }}>
                  {p.label}
                </span>
                {isActive && (
                  <svg style={{ marginLeft: 'auto' }} width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <polyline points="2,6 5,9 10,3" stroke={p.color} strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                )}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}
